import { Controller } from '@hotwired/stimulus';
import Vue from 'vue';
import { mountVueApp } from 'shared/vue/mountVueApp';

// purchase_history_component (下記) は shop_message.targets の v-for の中で動的に
// 生成/破棄されるため、独立した Stimulus 値を持てない。connect() でセットする。
let _targetIdToPurchasedProductIdsMap = null;

// ------------------------------------------------------------------------------
// 送信ターゲット: 購入履歴
// ------------------------------------------------------------------------------
// カテゴリーに紐づく商品名をtチェックボックスとともに表示する
const purchased_product_item = {
  el: 'purchased-product-item',
  template: `
      <li class="p-target-purchased-product__product--item">
        <input :id="fieldKey" v-model="isProductChecked" type="checkbox" class="c-checkbox__input" :value="product.id">
        <label :for="fieldKey" class="c-checkbox__label">
          <div class="p-target-purchased-product__truncate">
            {{ product.name }}
          </div>
        </label>
      </li>
      `,
  props: {
    product: {
      id: Number,
      name: String,
    },
    categoryId: Number,
    selectedProductIds: Set,
    serviceKey: String,
    targetIndex: Number,
  },
  computed: {
    fieldKey: function () {
      return `purchased-product-${this.serviceKey}-${this.categoryId}-${this.product.id}-${this.targetIndex}`;
    },
    isProductChecked: {
      get: function () {
        return this.selectedProductIds.has(this.product.id);
      },
      set: function (checked) {
        this.$emit('change', {
          categoryId: this.categoryId,
          productId: this.product.id,
          checked,
        });
      },
    },
  },
};

// カテゴリーに紐づく商品をまとめて表示するcomponentです
// HTMLの階層としてはカテゴリー名と同じ箇所に描画されます
const purchased_product_list = {
  el: 'purchased-product-list',
  components: {
    'purchased-product-item': purchased_product_item,
  },
  template: `
      <li class="p-target-purchased-product__product" :class="orderClassName">
        <ul class="p-target-purchased-product__product--list">
          <purchased-product-item
            v-for="product in products"
            :key="categoryId + product.id"
            @change="onProductChange"
            :product="product"
            :selected-product-ids="selectedProductIds"
            :category-id="categoryId"
            :service-key="serviceKey"
            :target-index="targetIndex"
          />
        </ul>
      </li>
      `,
  props: {
    selectedProductIds: Set,
    categoryId: Number,
    index: Number,
    products: Array,
    onProductChange: Function,
    serviceKey: String,
    targetIndex: Number,
  },
  computed: {
    orderClassName: function () {
      return `order-${this.index}`;
    },
  },
};

// カテゴリー名をチェックボックスとともに表示する
const purchased_category_item = {
  el: 'purchased-category-item',
  components: {
    'purchased-product-item': purchased_product_item,
  },
  template: `
      <li class="p-target-purchased-product__category--item" :class="[{'open': isProductOpen}, orderClassName]" @click="productOpenToggle(category.id)">
        <input :id="fieldKey" type="checkbox" class="c-checkbox__input" v-model="categoryModel">
        <label :for="fieldKey" class="c-checkbox__label p-target-purchased-product__checkbox" :class="anyCheckedProductClassName"></label>
        <div class="p-target-purchased-product__collapse">
          <i />
          <div class="p-target-purchased-product__truncate">
            {{ category.name }}
          </div>
        </div>
      </li>
      `,
  props: {
    category: {
      id: Number,
      name: String,
    },
    productOpenToggle: Function,
    isChecked: Boolean,
    isAnyChecked: Boolean,
    isProductOpen: Boolean,
    index: Number,
    serviceKey: String,
    targetIndex: Number,
  },
  computed: {
    fieldKey: function () {
      return `purchased-category-item-${this.serviceKey}-${this.category.id}-${this.targetIndex}`;
    },
    orderClassName: function () {
      return `order-${this.index}`;
    },
    categoryModel: {
      get: function () {
        return this.isChecked || this.isAnyChecked;
      },
      set: function (val) {
        return this.$emit('change', {
          categoryId: this.category.id,
          checked: val,
        });
      },
    },
    anyCheckedProductClassName: function () {
      if (!this.isChecked && this.isAnyChecked) {
        return 'any-checked';
      }
      return '';
    },
  },
};

// カテゴリーとそれに紐づく商品一覧をここで表示しています
// ここでカテゴリー一覧の開閉や商品一覧の開閉の制御や
// 紐づいている商品がチェックかれているかの状態とチェックされている場合のカテゴリーごとのチェックの状態を管理しています
const purchased_service = {
  el: 'purchased-service',
  components: {
    'purchased-category-item': purchased_category_item,
    'purchased-product-list': purchased_product_list,
  },
  template: `
      <fieldset class="p-target-purchased-product__item p-formA__item p-formA__item__s__12" v-show="isShow || isPreparing">
        <div ref="hiddenInputsContainer"></div>
        <div v-if="isShow">
          <div class="p-target-purchased-product__item--header" :class="{'open': isCategoryOpen}" @click="categoryOpenToggle">
            <input
              :id="fieldKey"
              type="checkbox"
              class="c-checkbox__input"
              @change="checkedAllCategoryToggle"
              :checked="isCategoryChecked"
              ref="allCheckbox"
              />
            <label :for="fieldKey" class="c-checkbox__label p-target-purchased-product__checkbox" :class="anyCheckedCategoryClassName"></label>
            <div class="p-target-purchased-product__collapse">
              <i />
              <div class="p-target-purchased-product__truncate">
                {{ title }}
              </div>
            </div>
          </div>
          <div v-if="isCategoryOpen" class="p-target-purchased-product__category">
            <ul class="c-checkbox p-target-purchased-product__category--list">
              <template v-for="(category, index) in categories">
                <purchased-category-item
                  :key="'cat-' + category.id"
                  :category="category"
                  :product-open-toggle="productOpenToggle"
                  :is-product-open="isOpenedCategoryId === category.id"
                  :index="index"
                  :is-checked="checkedAllCategory[category.id]"
                  :is-any-checked="checkedAnyCategory[category.id]"
                  @change="checkedCategoryToggle"
                  :service-key="serviceKey"
                  :target-index="targetIndex"
                />
                <purchased-product-list
                  v-if="isOpenedCategoryId === category.id"
                  :key="'prod-' + category.id"
                  :selected-product-ids="selectedProductIds"
                  :category-id="category.id"
                  :index="index"
                  :products="allProductsPerCategory[category.id]"
                  :on-product-change="onProductChange"
                  :service-key="serviceKey"
                  :target-index="targetIndex"
                />
              </template>
            </ul>
          </div>
        </div>
        <div v-else-if="isPreparing">
          <div class="p-target-purchased-product__item--header">
            <input
              type="checkbox"
              class="c-checkbox__input"
              disabled
              />
            <label class="c-checkbox__label p-target-purchased-product__checkbox"></label>
            <div class="p-target-purchased-product__loading">
              <i class="fa fa-spinner fa-spin" />
              <div class="p-target-purchased-product__truncate">
                {{ title }} (読込中...)
              </div>
            </div>
          </div>
        </div>
      </fieldset>
      `,
  props: {
    title: String,
    serviceKey: String,
    targetIndex: Number,
    targetId: Number,
  },
  data: () => ({
    selectedProductIds: new Set(),
    categories: [],
    isCategoryOpen: false,
    isOpenedCategoryId: null,
    checkedAllCategory: {},
    checkedAnyCategory: {},
    allProductsPerCategory: {},
    allProductIds: new Set(),
    productIdToCategoryIdsMap: {},
    isLoading: true,
    isPreparing: false,
  }),
  watch: {
    selectedProductIds(newIds) {
      this.updateHiddenInputs(newIds);
    },
  },
  mounted: function () {
    this.updateHiddenInputs(this.selectedProductIds);
  },
  created: async function () {
    this.isLoading = true;

    try {
      // 初期に選択されている商品IDをSetとして設定
      const ids_map = _targetIdToPurchasedProductIdsMap || {};
      const initialIds = ids_map[this.targetId]?.[this.serviceKey] || [];
      this.selectedProductIds = new Set(initialIds);

      // 非同期で全カテゴリー・商品データを取得
      const allData = await this.fetchAllCategoriesWithProducts();

      const categories = [];
      const allProductsPerCategory = {};
      const allProductIds = new Set();
      const checkedAllCategory = {};
      const checkedAnyCategory = {};
      const productIdToCategoryIdsMap = {};

      for (const categoryWithProducts of allData) {
        const category = categoryWithProducts.category;
        const products = categoryWithProducts.products;
        categories.push(category);
        allProductsPerCategory[category.id] = products;

        for (const product of products) {
          allProductIds.add(product.id);
          if (!productIdToCategoryIdsMap[product.id]) {
            productIdToCategoryIdsMap[product.id] = new Set();
          }
          productIdToCategoryIdsMap[product.id].add(category.id);
        }
        checkedAllCategory[category.id] = products.every((p) =>
          this.selectedProductIds.has(p.id),
        );
        checkedAnyCategory[category.id] = products.some((p) =>
          this.selectedProductIds.has(p.id),
        );
      }

      this.categories = categories;
      this.allProductsPerCategory = allProductsPerCategory;
      this.allProductIds = new Set(allProductIds);
      this.checkedAllCategory = checkedAllCategory;
      this.checkedAnyCategory = checkedAnyCategory;
      this.productIdToCategoryIdsMap = productIdToCategoryIdsMap;
    } catch (error) {
      console.error('Error fetching data:', error);
      alertify.error('エラー');
    } finally {
      this.isPreparing = false;
      this.isLoading = false;
    }
  },
  computed: {
    isShow: function () {
      return !this.isLoading && this.categories.length > 0;
    },
    isAllCategoryChecked: function () {
      if (this.allProductIds.size === 0) return false;
      return this.selectedProductIds.size === this.allProductIds.size;
    },
    isAnyCategoryChecked: function () {
      return this.selectedProductIds.size > 0;
    },
    isCategoryChecked: function () {
      return this.isAllCategoryChecked || this.isAnyCategoryChecked;
    },
    isIndeterminate: function () {
      return this.isAnyCategoryChecked && !this.isAllCategoryChecked;
    },
    anyCheckedCategoryClassName: function () {
      return this.isIndeterminate ? 'any-checked' : '';
    },
    fieldKey: function () {
      return `purchased-service-${this.title}-${this.targetIndex}`;
    },
    productClassName: function () {
      if (this.serviceKey === 'shopify') {
        return 'Shopify::Product';
      }
      if (this.serviceKey === 'smaregi') {
        return 'SmaregiProduct';
      }
      if (this.serviceKey === 'pos') {
        return 'GenericPosProduct';
      }
      if (this.serviceKey === 'stores_ec') {
        return 'Stores::Retail::Item';
      }
    },
    productsPath: () => '/api/shop/v1/shop_card/purchased_products',
  },
  methods: {
    updateHiddenInputs: function (ids) {
      const container = this.$refs.hiddenInputsContainer;
      if (!container) return;

      container.innerHTML = '';
      if (ids.size === 0) return;

      const fragment = document.createDocumentFragment();
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name =
        'shop_message[targets_attributes][][purchase_product_ids][][type]';
      input.value = this.productClassName;
      fragment.appendChild(input);

      for (const id of ids) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name =
          'shop_message[targets_attributes][][purchase_product_ids][][ids][]';
        input.value = id;
        fragment.appendChild(input);
      }

      container.appendChild(fragment);
    },
    // 商品がチェックされたときの処理
    onProductChange: function ({ productId, checked }) {
      if (checked) {
        this.selectedProductIds.add(productId);
      } else {
        this.selectedProductIds.delete(productId);
      }
      const relatedCategoryIds =
        this.productIdToCategoryIdsMap[productId] || [];
      for (const categoryId of relatedCategoryIds) {
        this.updateCategoryState(categoryId);
      }
      this.selectedProductIds = new Set(this.selectedProductIds);
    },
    // カテゴリのチェック状態を更新
    updateCategoryState: function (categoryId) {
      const productsInCategory = this.allProductsPerCategory[categoryId];
      if (!productsInCategory) return;

      const allChecked =
        productsInCategory.length > 0 &&
        productsInCategory.every((p) => this.selectedProductIds.has(p.id));
      const anyChecked =
        allChecked ||
        productsInCategory.some((p) => this.selectedProductIds.has(p.id));
      this.$set(this.checkedAllCategory, categoryId, allChecked);
      this.$set(this.checkedAnyCategory, categoryId, anyChecked);
    },
    // カテゴリのチェックボックスが変更されたときの処理
    checkedCategoryToggle: function ({ categoryId, checked }) {
      const productsToToggle = this.allProductsPerCategory[categoryId];
      const affectedCategoryIds = new Set();
      productsToToggle.forEach((p) => {
        if (checked) {
          this.selectedProductIds.add(p.id);
        } else {
          this.selectedProductIds.delete(p.id);
        }
        const relatedIds = this.productIdToCategoryIdsMap[p.id] || [];
        relatedIds.forEach((id) => {
          affectedCategoryIds.add(id);
        });
      });
      affectedCategoryIds.forEach((catId) => {
        this.updateCategoryState(catId);
      });
      this.selectedProductIds = new Set(this.selectedProductIds);
    },
    // カテゴリ全体のチェックボックスが変更されたときの処理
    checkedAllCategoryToggle: function (event) {
      const isChecked = event.target.checked;
      if (isChecked) {
        this.selectedProductIds = new Set(this.allProductIds);
      } else {
        this.selectedProductIds = new Set();
      }
      // 各カテゴリーの状態も一括更新
      for (const categoryId in this.allProductsPerCategory) {
        this.$set(this.checkedAllCategory, categoryId, isChecked);
        this.$set(this.checkedAnyCategory, categoryId, isChecked);
      }
    },
    // APIから全てのカテゴリーと商品を取得
    fetchAllCategoriesWithProducts: async function () {
      let currentPage = 1;
      let totalPages = 1;
      const allCategories = [];
      while (currentPage <= totalPages) {
        const data = await $.ajax({
          type: 'GET',
          url: this.productsPath,
          data: { page: currentPage, product_type: this.serviceKey },
          dataType: 'json',
        });
        if (data?.categories && data.categories.length > 0) {
          this.isPreparing = true;
          allCategories.push(...data.categories);
        }
        totalPages = data?.pagination?.total_pages || 0;
        currentPage++;
      }
      return allCategories;
    },
    // カテゴリーの開閉トグル
    categoryOpenToggle: function () {
      this.isCategoryOpen = !this.isCategoryOpen;
    },
    // 商品一覧の開閉トグル
    productOpenToggle: function (categoryId) {
      this.isOpenedCategoryId =
        this.isOpenedCategoryId === categoryId ? null : categoryId;
    },
  },
};

// 各サービスを設定する箇所です
// 基本的には purchased-serviceを増やすだけで使用できるサービスを増やすことができます
const purchase_history_component = {
  el: 'purchase-history',
  components: {
    'purchased-service': purchased_service,
  },
  template: `
      <div class="p-target-purchased-product p-formA__items">
        <purchased-service
          title="POS"
          :target-index="targetIndex"
          :target-id="targetId"
          service-key="pos"
        />
        <purchased-service
          title="スマレジ"
          :target-index="targetIndex"
          :target-id="targetId"
          service-key="smaregi"
        />
        <purchased-service
          title="Shopify"
          :target-index="targetIndex"
          :target-id="targetId"
          service-key="shopify"
        />
        <purchased-service
          title="STORES"
          :target-index="targetIndex"
          :target-id="targetId"
          service-key="stores_ec"
        />
      </div>
      `,
  props: {
    targetIndex: Number,
    targetId: Number,
  },
};

export default class extends Controller {
  static targets = ['template'];
  static values = {
    shopMessage: Object,
    shopBranches: Array,
    shopBranchGroups: Array,
    shopCoupons: Array,
    targetAttributes: Object,
    shopMessageEyecatch: String,
    shopCampaigns: Array,
    targetIdToPurchasedProductIdsMap: Object,
  };

  connect() {
    const shopMessage = this.shopMessageValue;
    const shopBranches = this.shopBranchesValue;
    const shopBranchGroups = this.shopBranchGroupsValue;
    const shopCoupons = this.shopCouponsValue;
    const targetAttributes = this.targetAttributesValue;
    const shopMessageEyecatch = this.shopMessageEyecatchValue || null;
    const shopCampaigns = this.shopCampaignsValue;

    _targetIdToPurchasedProductIdsMap =
      this.targetIdToPurchasedProductIdsMapValue;

    this.vueApp = mountVueApp(this.templateTarget, {
      components: {
        'purchase-history': purchase_history_component,
      },
      data: {
        shop_message: {
          // ビュー側で参照するプロパティは初期値をセット(初期値ないとエラーになる)
          trigger_type: null,
          targets: [],
          shop_coupon: {
            _destroy: false,
          },
          shop_message_shop_campaign: {
            _destroy: false,
          },
          shop_message_shop_lotto: {
            _destroy: false,
          },
        },
        draft_btn_str: '',
        action_label_str: '',
        shop_message_eyecatch: null,
        shop_coupon_picture: null,
        show_trigger_options: false,
        shop_message_text: '',
        exist_valid_error: null,
        shop_visited_easy_mode: true,
        display_detail_setting: true,
        shop_visited_easy_mode_label: '詳細指定に切替（上級者向け）',
        is_loading: false,
        shop_campaigns: [],
        select_options_for_campaigns: [],
        shop_branches: [],
        shop_branch_groups: [],
        shop_coupons: [],
        shop_items: [],
        shop_item_categories: [],
        shop_message_interval: {},
      },
      computed: {
        previewEyecatch() {
          return this.shop_message_eyecatch || '';
        },
        shopMessageIntervalMin() {
          switch (this.shop_message_interval.interval_type) {
            case 'unspecified':
              return null;
            case 'interval':
              return this.shop_message_interval.interval_day * 60 * 24;
            case 'once':
              return 90_909_090;
          }
        },
      },
      mounted: function () {
        this.initialize();
      },
      destroyed: () => {
        if (CKEDITOR.instances.shop_message_text) {
          CKEDITOR.instances.shop_message_text.destroy();
        }
      },
      methods: {
        initialize: function () {
          this.disable_enter_submit();
          this.shop_message_init();
          this.target_init();
          this.trigger_init();
          this.ckeditor_init();
        },
        ckeditor_init: () => {
          CKEDITOR.env.isCompatible = true;
          CKEDITOR.replace('shop_message[text]');
          // Ckeditorに絵文字ボタンを追加
          CKEDITOR.on('instanceReady', (e) => {
            $('#cke_1_toolbox').append(
              '<span class="cke_toolbar_separator" role="separator"></span><button name="button" type="button" style="line-height:28px;padding:0 8px;" onClick="$(\'#emoji_modal\').modal()">😄</button>',
            );
          });
        },
        disable_enter_submit: () => {
          // textareaは対象外
          $.each(['input', 'select'], (index, el) => {
            $(document).on('keypress', `${el}:not(.allow_submit)`, (e) => {
              if (
                (e.which && e.which === 13) ||
                (e.keyCode && e.keyCode === 13)
              )
                return false;
              return true;
            });
          });
        },
        shop_message_init: function () {
          this.$set(this, 'shop_message', shopMessage);
          this.$set(this, 'shop_campaigns', shopCampaigns);
          this.$set(this, 'shop_message_eyecatch', shopMessageEyecatch);
          this.$set(this, 'shop_branches', shopBranches);
          this.$set(this, 'shop_branch_groups', shopBranchGroups);
          this.$set(this, 'shop_coupons', shopCoupons);

          let interval_type;
          let interval_day = 1;

          switch (shopMessage.interval_min) {
            case null:
              interval_type = 'unspecified';
              break;
            case '90909090':
              interval_type = 'once';
              break;
            default:
              interval_type = 'interval';
              interval_day = Math.floor(
                Number.parseInt(shopMessage.interval_min, 10) / (24 * 60),
              );
              break;
          }

          this.$set(this, 'shop_message_interval', {
            interval_type,
            interval_day,
          });
        },
        target_init: function () {
          this.target_attributes = $.extend({}, targetAttributes, {
            last_visited_easy_setting: '',
            last_visited_from: 0,
          });

          if (this.shop_message.targets.length > 0) {
            const throughKeys = [
              'id',
              'targetable_type',
              'targetable_id',
              'expire_coupon_shop_coupon_ids',
              'last_visited_shop_branch_ids',
              'purchase_shop_branch_ids',
              'visited_cnt_shop_branch_ids',
              'purchase_product_ids',
              'optional_cond',
              'last_visited_from',
            ];

            for (const target of this.shop_message.targets) {
              // デフォ選択状態対策
              for (const key of Object.keys(target)) {
                if (throughKeys.indexOf(key) >= 0) continue;

                // Rails側では true or false なんだけど、JS側では 1 or 0 で扱われる問題のワークアラウンド ＼(^o^)／
                if (key === 'last_visited_only') {
                  if (target[key] === true) {
                    target[key] = '1';
                  } else if (target[key] === false) {
                    target[key] = '0';
                  }
                }

                if (target[key] === 0) {
                  // 数値0を文字列にリプレイス(false判定されないための対策)
                  target[key] = '0';
                } else if (target[key] === null || target[key] === undefined) {
                  // null or undefinedを空文字にリプレイス
                  target[key] = '';
                }
              }

              // モデルに選択済みの来店履歴の時間(last_visited_***)を設定
              this.set_last_visited_time(target);

              // モデルに選択済みの有効期限を迎えるクーポンid(shop_coupon.id)を設定
              this.set_expire_coupon_shop_coupon_ids_by(target);
              // アンケート選択時のレンジオプション設定
              Vue.set(target, 'show', true);
              Vue.set(target, '_destroy', false);
              Vue.set(
                target,
                'campaign_unit_from',
                target.campaign_unit_from_enum_str || '',
              );
              Vue.set(
                target,
                'campaign_unit_to',
                target.campaign_unit_to_enum_str || '',
              );
              Vue.set(
                target,
                'campaign_condition_from',
                target.campaign_condition_from_enum_str || '',
              );
              Vue.set(
                target,
                'campaign_condition_to',
                target.campaign_condition_to_enum_str || '',
              );
              Vue.set(
                target,
                'campaign_date_comparison',
                target.campaign_date_comparison_enum_str || '',
              );
              Vue.set(
                target,
                'is_range_campaign',
                !!(
                  target.campaign_val_to !== null &&
                  target.campaign_val_to !== '0'
                ),
              );
              Vue.set(
                target,
                'select_options_for_campaigns',
                this.get_options_value_by_campaign_id(target.shop_campaign_id),
              );
              Vue.set(target, 'optional_cond', true);
              Vue.set(
                target,
                'last_visited_from',
                target.last_visited_from || 0,
              );
              this.set_selected_shop_campaign_item_type(target);
            }
          }
        },
        trigger_init: function () {
          this.display_detail_setting = this.is_display_trigger_detail();
          this.changeTriggerType();
        },
        set_last_visited_time: function (target) {
          if (
            typeof target.last_visited_from === 'number' &&
            typeof target.last_visited_to === 'number'
          ) {
            // 簡易モード
            if (
              (target.last_visited_to - target.last_visited_from) % 1440 ===
              0
            ) {
              const day = target.last_visited_to / 1440;
              target.last_visited_easy_setting = day;
              this.activateShopVisitedEasyMode(true, target);
              // 詳細モード
            } else {
              this.activateShopVisitedEasyMode(false, target);
            }
          } else if (
            typeof target.last_visited_from === 'number' &&
            typeof target.last_visited_to !== 'number'
          ) {
            this.activateShopVisitedEasyMode(false, target);
          }
        },
        set_expire_coupon_shop_coupon_ids_by: function (target) {
          if (target.type !== 'expire_coupon') {
            return;
          }

          target.isAllSelected =
            target.expire_coupon_shop_coupon_ids.length ===
            this.shop_coupons.length;
        },
        addConditionField: function () {
          const targets = this.shop_message.targets;
          const target_options = $.extend(
            {
              show: true,
              _destroy: false,
              type: '',
              optional_cond: false,
              selected_campaign_item_type: null,
              shop_campaign_id: null,
            },
            this.target_attributes,
          );
          // if (targets.length === 0) targets = [];

          targets.push(target_options);
        },
        removeConditionField: function (target, index) {
          target.show = false;
          target._destroy = 1;
          const _target = Object.assign({}, target);

          this.shop_message.targets.splice(index, 1, _target);
        },
        resetTargetAttrs: function (target) {
          const keys = Object.keys(this.target_attributes);
          const throughKeys = ['id', 'targetable_type', 'targetable_id'];
          const arrayValueKeys = [
            'expire_coupon_shop_coupon_ids',
            'last_visited_shop_branch_ids',
            'purchase_shop_branch_ids',
            'visited_cnt_shop_branch_ids',
          ];
          const booleanValueKeys = [];

          for (const key of keys) {
            // keyがターゲットのプロパティとして存在しない or リセット対象外の場合は何もしない
            if (!Object.hasOwn(target, key) || throughKeys.indexOf(key) >= 0)
              return;

            if (arrayValueKeys.indexOf(key) >= 0) {
              target[key] = [];
            } else if (booleanValueKeys.indexOf(key) >= 0) {
              target[key] = null;
            } else {
              target[key] = '';
            }
          }
        },
        previewMessage: async function (path) {
          $('#shop_message_preview_modal').modal();

          this.setShopMessageText();

          $(
            '#shop_message_preview_modal .modal-content .modal-body .contents_wrap',
          ).html('');
          this.is_loading = true;
          this.exist_valid_error = true;

          // アイキャッチの画像ファイルまでプレビューに送らないよう、文字列の項目だけを送る
          const body = new URLSearchParams();
          for (const [name, value] of new FormData(
            document.querySelector('form.bulk_shop_message_form'),
          )) {
            if (typeof value === 'string') body.append(name, value);
          }

          let data;
          try {
            const response = await fetch(path, {
              method: 'POST',
              headers: { Accept: 'text/html' },
              body,
            });
            if (!response.ok) throw new Error(response.statusText);
            data = await response.text();
          } catch (error) {
            console.error(error);
            alertify.error('エラー');
            this.is_loading = false;
            return;
          }

          this.is_loading = false;
          $('#shop_message_preview_modal .modal-content .modal-body').html(
            data,
          );
          // 保存エラーで再描画した画面の上部にも同じクラスのエラー枠が残るため、プレビュー内だけを見る
          if (
            $('#shop_message_preview_modal .c-alertBox__content').length > 0
          ) {
            this.exist_valid_error = true;
          } else {
            this.exist_valid_error = false;
          }

          // プレビュー内のsubmitボタンのテキスト設定
          const trigger = this.shop_message.trigger_type;
          if (trigger === 'now') {
            this.action_label_str = !this.shop_message.sent_at
              ? '送信'
              : '保存';
            this.draft_btn_str = '下書き保存';
          } else if (trigger === 'reserve') {
            this.action_label_str = '保存';
            this.draft_btn_str = '下書き保存';
          } else {
            this.action_label_str = '保存&配信開始';
            this.draft_btn_str = '保存&配信停止';
          }

          const shop_message_eyecatch_img = this.shop_message_eyecatch;
          if (shop_message_eyecatch_img) {
            $('#shop_message_eyecatch_img_preview').attr({
              src: shop_message_eyecatch_img,
            });
          } else {
            $('#shop_message_eyecatch_img_preview').closest('.eyecatch').hide();
          }

          if (this.shop_coupon_picture) {
            $('.coupon_area img.picture').attr({
              src: this.shop_coupon_picture,
            });
          }
        },
        changeTriggerType: function () {
          // 現在選択されたタイプとは関係のない項目の値をnilにする
          const shop_message = this.shop_message;
          const current_trigger_type = shop_message.trigger_type;
          // トリガー項目でアクティブにする項目を判別するためのリスト
          let white_list = this.create_white_list_for_trigger();
          // リストから選択されたトリガータイプに紐づく項目情報を取得
          white_list = white_list[current_trigger_type].concat(
            white_list.common,
          );

          // 全項目を非表示
          $('.trigger_item').removeClass('active');

          for (const key of Object.keys(shop_message)) {
            if (
              key === 'targets' ||
              key === 'shop_coupon' ||
              key === 'shop_message_shop_campaign' ||
              key === 'shop_message_shop_lotto'
            )
              continue;

            // ホワイトリストに指定あり
            if (white_list.indexOf(key) >= 0) {
              // 値がnullなら初期値セット
              if (!shop_message[key]) {
                shop_message[key] = '';
              }

              // 表示
              $(`.trigger_item.${key}`).addClass('active');
            } else {
              // 値がnullなら初期値セット
              if (!shop_message[key]) {
                shop_message[key] = '';
              }
            }
          }
          switch (current_trigger_type) {
            case 'now':
              this.shop_message.trigger_type = 'now';
              break;
            case 'reserve':
              this.shop_message.trigger_type = 'reserve';
              break;
            case 'month':
              this.shop_message.trigger_type = 'month';
              this.shop_message.scheduled_wday = '';
              break;
            case 'week':
              this.shop_message.trigger_type = 'week';
              this.shop_message.scheduled_day = '';
              break;
            case 'day':
              this.shop_message.trigger_type = 'day';
              this.shop_message.scheduled_wday = '';
              this.shop_message.scheduled_day = '';
              break;
            case 'rain':
              this.shop_message.trigger_type = 'rain';
              break;
            case 'temp':
              this.shop_message.trigger_type = 'temp';
              break;
            case 'get_stamp':
              this.shop_message.trigger_type = 'get_stamp';
              break;
            case 'get_card':
              this.shop_message.trigger_type = 'get_card';
              break;
            case 'consume_coupon':
              this.shop_message.trigger_type = 'consume_coupon';
              break;
            case 'answer_campaign':
              this.shop_message.trigger_type = 'answer_campaign';
              break;
            case 'play_lotto':
              this.shop_message.trigger_type = 'play_lotto';
              break;
            default:
              this.shop_message.trigger_type = 'now';
              break;
          }
          // 詳細項目の表示判定
          if (this.is_display_trigger_detail()) {
            this.display_detail_setting = true;
          } else {
            this.display_detail_setting = false;
          }
        },
        change_shop_campaign: function (e, target) {
          const selected_shop_campaign_id = $(e.currentTarget)
            .find(':selected')
            .val();

          // デフォルトでselectedにするoption
          const current_shop_campaign = this.shop_campaigns.find(
            (shop_campaign) =>
              shop_campaign.id ===
              Number.parseInt(selected_shop_campaign_id, 10),
          );
          target.shop_campaign_item_id =
            current_shop_campaign.shop_campaign_items[0].id;
          Vue.set(
            target,
            'select_options_for_campaigns',
            this.get_options_value_by_campaign_id(target.shop_campaign_id),
          );

          this.set_selected_shop_campaign_item_type(target);
          this.reset_campaign_val(target);
        },
        get_options_value_by_campaign_id: function (campaign_id) {
          const result = this.shop_campaigns.find(
            (campaign) => campaign.id === Number.parseInt(campaign_id, 10),
          );
          return result ? result.shop_campaign_items : null;
        },
        set_selected_shop_campaign_item_type: function (target) {
          if (!target.shop_campaign_item_id) return;
          const selected_shop_campaign_id = target.shop_campaign_id;
          const selected_shop_campaign = this.shop_campaigns.find(
            (campaign) =>
              campaign.id === Number.parseInt(selected_shop_campaign_id, 10),
          );
          const selected_shop_campaign_item =
            selected_shop_campaign.shop_campaign_items.find(
              (shop_campaign_item) =>
                shop_campaign_item.id === target.shop_campaign_item_id,
            );

          Vue.set(
            target,
            'selected_campaign_item_type',
            selected_shop_campaign_item.item_type,
          );
        },
        change_shop_campaign_item: function (parent_target) {
          this.set_selected_shop_campaign_item_type(parent_target);
          this.reset_campaign_val(parent_target);
        },
        reset_campaign_val: (target) => {
          Vue.set(target, 'campaign_val_from', '');
          Vue.set(target, 'campaign_val_to', '');
          Vue.set(target, 'campaign_condition_from', '');
          Vue.set(target, 'campaign_condition_to', '');
          Vue.set(target, 'campaign_date_comparison', '');
          Vue.set(target, 'campaign_unit_from', '');
          Vue.set(target, 'campaign_unit_to', '');
        },
        change_shop_campaign_items_range_option: (target) => {
          if (target.is_range_campaign) target.campaign_val_to = '';
        },
        is_display_trigger_detail: function () {
          const trigger_type = this.shop_message.trigger_type;
          return !($.inArray(trigger_type, ['now', 'reserve']) >= 0);
        },
        changeLastVisitedDate: (target) => {
          const selected_day = target.last_visited_easy_setting;
          if (!selected_day) {
            target.last_visited_from = '';
            target.last_visited_to = '';
          } else {
            const last_visited_from =
              (Number.parseInt(selected_day, 10) - 1) * 1440;
            target.last_visited_from = String(last_visited_from);
            target.last_visited_to = String(last_visited_from + 1440);
          }
        },
        showTriggerOptions: function () {
          this.show_trigger_options = true;
        },
        toggleShopVisitedDetailSetting: function (target) {
          // 一旦クリア
          target.last_visited_easy_setting = '';
          target.last_visited_from = '';
          target.last_visited_to = '';

          // モード切替
          this.activateShopVisitedEasyMode(
            !this.shop_visited_easy_mode,
            target,
          );
        },
        activateShopVisitedEasyMode: function (flg, target) {
          if (flg) {
            this.shop_visited_easy_mode = true;
            this.shop_visited_easy_mode_label = '詳細指定に切替（上級者向け）';
          } else {
            this.shop_visited_easy_mode = false;
            this.shop_visited_easy_mode_label = '簡易指定に切替';
            if (!target.last_visited_from) {
              target.last_visited_from = 0;
            }
          }
        },
        toggleVisitHistoryTargetDetailCond: function (target) {
          this.toggleTargetDetailCond(target);
          if (target.optional_cond) {
            if (!target.last_visited_from) {
              target.last_visited_from = 0;
            }
          } else {
            target.last_visited_from = '';
          }
        },
        toggleTargetDetailCond: (target) => {
          target.optional_cond = !target.optional_cond;
        },
        changeShopMessageEyecatch: function (e) {
          const files = e.target.files || e.dataTransfer.files;
          if (!files.length) {
            this.shop_message_eyecatch = null;
            $('#shop_message_eyecatch_img_preview').attr({ src: null });
            return;
          }
          const reader = new FileReader();
          reader.onload = (e) => {
            this.shop_message_eyecatch = e.target.result;
            document.getElementById('shop_message_eyecatch_delete').value =
              null;
          };
          reader.readAsDataURL(files[0]);
        },
        clearShopMessageEyecatch: function () {
          document.getElementById('shop_message_eyecatch_delete').value = '1';
          document.getElementById('shop_message_eyecatch').value = '';
          this.shop_message_eyecatch = null;
        },
        changeShopCouponPic: function (e) {
          const files = e.target.files || e.dataTransfer.files;
          if (!files.length) {
            this.shop_coupon_picture = null;
            $('.coupon_area img.picture').attr({ src: null });
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            this.shop_coupon_picture = e.target.result;
            document.getElementById('coupon_picture_delete').value = null;
          };
          reader.readAsDataURL(files[0]);
        },
        clearShopCouponPic: function () {
          document.getElementById('coupon_picture_delete').value = '1';
          document.getElementById('coupon_picture').value = '';
          this.shop_coupon_picture = null;
        },
        showShopCouponAdvanced: () => {
          $('.advanced_options_coupon_btn').hide();
          $('.advanced_options_coupon').show();
        },
        setShopMessageText: function () {
          // NOTE: ckeditorとv-modelを共存させるために
          //       ワークアラウンド感は否めない
          this.shop_message_text =
            CKEDITOR.instances.shop_message_text.getData();
          $('#shop_message_text').val(
            CKEDITOR.instances.shop_message_text.getData(),
          );
        },
        setAllShopBranchIds: function (e, target) {
          if (e.target.checked) {
            target.group_checkboxes = [e.target.value];
            let targetModel = null;

            if (target.type === 'visited_shop') {
              targetModel = target.last_visited_shop_branch_ids;
            }
            if (target.type === 'visited_shop_include_checkin') {
              targetModel = target.last_visited_shop_branch_ids;
            }
            if (target.type === 'purchase_count') {
              targetModel = target.purchase_shop_branch_ids;
            }
            if (target.type === 'visited_count') {
              targetModel = target.visited_cnt_shop_branch_ids;
            }

            if (!target.type) {
              throw new Error('Unknown target type');
            }

            targetModel.splice(0, targetModel.length);
            for (const shop_branch of this.shop_branches) {
              targetModel.push(shop_branch.id);
            }
          } else {
            if (
              target.type === 'visited_shop' ||
              target.type === 'visited_shop_include_checkin'
            ) {
              target.last_visited_shop_branch_ids = [];
            } else if (target.type === 'visited_count') {
              target.visited_cnt_shop_branch_ids = [];
            } else if (target.type === 'purchase_count') {
              target.purchase_shop_branch_ids = [];
            } else {
              throw new Error('Unknown target type');
            }
          }
        },
        setShopBranchIds: (e, target, shop_branch_ids) => {
          if (e.target.checked) {
            target.group_checkboxes = [e.target.value];
            let targetModel = null;

            if (target.type === 'visited_shop') {
              targetModel = target.last_visited_shop_branch_ids;
            }
            if (target.type === 'visited_shop_include_checkin') {
              targetModel = target.last_visited_shop_branch_ids;
            }
            if (target.type === 'purchase_count') {
              targetModel = target.purchase_shop_branch_ids;
            }
            if (target.type === 'visited_count') {
              targetModel = target.visited_cnt_shop_branch_ids;
            }

            if (!target.type) {
              throw new Error('Unknown target type');
            }

            targetModel.splice(0, targetModel.length);
            for (const shop_branch_id of shop_branch_ids) {
              targetModel.push(shop_branch_id);
            }
          } else {
            if (
              target.type === 'visited_shop' ||
              target.type === 'visited_shop_include_checkin'
            ) {
              target.last_visited_shop_branch_ids = [];
            } else if (target.type === 'visited_count') {
              target.visited_cnt_shop_branch_ids = [];
            } else if (target.type === 'purchase_count') {
              target.purchase_shop_branch_ids = [];
            } else {
              throw new Error('Unknown target type');
            }
          }
        },

        setAllShopCouponIds: function (e, target) {
          target.expire_coupon_shop_coupon_ids = e.target.checked
            ? this.shop_coupons.map((c) => c.id)
            : [];
        },

        // TODO: 購入履歴の商品を選択するチェックボックスの処理
        //       UIに関しては現実装では商品のみの条件で、今後さらに条件などが追加されるため仮実装
        //       大きく見た目が変わるので、UIが決定したあとここを修正、削除する
        setAllPurchaseItemIds: function (e, target) {
          if (e.target.checked) {
            target.group_checkboxes = this.shop_item_categories
              .map((category) => `${category.id}`)
              .push('0');
            target.purchase_product_ids = this.shop_items.map(
              (item) => `${item.id}`,
            );
          } else {
            target.group_checkboxes = [];
            target.purchase_product_ids = [];
          }
        },
        setPurchaseItemIds: function (e, target, shop_item_ids) {
          target.purchase_product_ids = target.purchase_product_ids.map(
            (id) => `${id}`,
          );
          if (e.target.checked) {
            if (Array.isArray(target.group_checkboxes)) {
              target.group_checkboxes.push(e.target.value);
            } else {
              target.group_checkboxes = [e.target.value];
            }

            const uniq_ids = new Set(target.purchase_product_ids);
            for (const id of shop_item_ids) {
              uniq_ids.add(`${id}`);
            }
            target.purchase_product_ids = Array.from(uniq_ids);
          } else {
            if (Array.isArray(target.group_checkboxes)) {
              target.group_checkboxes = target.group_checkboxes.filter(
                (c) => c !== e.target.value,
              );
            } else {
              target.group_checkboxes = this.shop_item_categories
                .map((category) => `${category.id}`)
                .filter((c) => c !== e.target.value);
            }
            const shop_item_ids_to_string = shop_item_ids.map(
              (sid) => `${sid}`,
            );
            target.purchase_product_ids = target.purchase_product_ids.filter(
              (id) => !shop_item_ids_to_string.includes(id),
            );
          }
        },

        resetPurchaseProductIds: (target) => {
          target.purchase_product_ids = [
            { type: 'Shopify::Product', ids: [] },
            { type: 'SmaregiProduct', ids: [] },
            { type: 'GenericPosProduct', ids: [] },
            { type: 'Stores::Retail::Item', ids: [] },
          ];
        },

        create_white_list_for_trigger: () => ({
          now: [],
          reserve: [
            'scheduled_at',
            'scheduled_at_1i',
            'scheduled_at_2i',
            'scheduled_at_3i',
            'scheduled_at_4i',
            'scheduled_at_5i',
          ],
          rain: ['rainy_shop_branch_id', 'scheduled_hour', 'scheduled_min'],
          month: ['scheduled_day', 'scheduled_hour', 'scheduled_min'],
          week: ['scheduled_hour', 'scheduled_min', 'scheduled_wday'],
          day: ['scheduled_hour', 'scheduled_min'],
          temp: [
            'temp_shop_branch_id',
            'temp_type',
            'temp_val',
            'temp_op',
            'scheduled_hour',
            'scheduled_min',
          ],
          get_stamp: ['target_shop_barcode_id', 'target_shop_barcode_group_id'],
          get_card: [],
          consume_coupon: ['target_shop_coupon_id'],
          answer_campaign: ['target_shop_campaign_id'],
          play_lotto: ['target_shop_lotto_item_id'],
          common: ['trigger_type', 'title', 'text'],
        }),
      },
    });
  }

  // 絵文字パレットは Vue のマウント先（form）の外にあるため、Stimulus のアクションで受ける
  insertEmoji({ params: { emoji } }) {
    CKEDITOR.instances.shop_message_text.insertText(emoji);
    $('#emoji_modal').modal('hide');
  }

  disconnect() {
    this.vueApp?.unmount();
  }
}
